import { useCallback } from "react";
import { PLACEMENT_ACTIONS } from "./usePlacementReducer";

/**
 * Custom hook for editing a specific section
 * Provides convenient helpers for updating fields, arrays, and nested data
 */
export function useSectionEditor(sectionIndex, dispatch) {
  // Update a top-level field in the section
  const updateField = useCallback(
    (field, value) => {
      dispatch({
        type: PLACEMENT_ACTIONS.UPDATE_SECTION_FIELD,
        sectionIndex,
        field,
        value,
      });
    },
    [dispatch, sectionIndex]
  );

  // Update an item in an array field (e.g., premiums[2].type = "X")
  const updateArrayItem = useCallback(
    (arrayField, itemIndex, field, value) => {
      dispatch({
        type: PLACEMENT_ACTIONS.UPDATE_SECTION_ARRAY_ITEM,
        sectionIndex,
        arrayField,
        itemIndex,
        nestedField: field,
        value,
      });
    },
    [dispatch, sectionIndex]
  );

  // Replace an entire item in an array
  const replaceArrayItem = useCallback(
    (arrayField, itemIndex, newValue) => {
      dispatch({
        type: PLACEMENT_ACTIONS.UPDATE_SECTION_ARRAY_ITEM,
        sectionIndex,
        arrayField,
        itemIndex,
        value: newValue,
      });
    },
    [dispatch, sectionIndex]
  );

  // Update nested array (e.g., premiums[i].instalments[j].field = value)
  const updateNestedArrayItem = useCallback(
    (parentArrayField, parentItemIndex, childArrayField, childItemIndex, field, value) => {
      dispatch({
        type: PLACEMENT_ACTIONS.UPDATE_NESTED_ARRAY_ITEM,
        sectionIndex,
        parentArrayField,
        parentItemIndex,
        childArrayField,
        childItemIndex,
        field,
        value,
      });
    },
    [dispatch, sectionIndex]
  );

  // Add a new item to an array field
  const addArrayItem = useCallback(
    (arrayField, newItem) => {
      dispatch({
        type: PLACEMENT_ACTIONS.ADD_SECTION_ARRAY_ITEM,
        sectionIndex,
        arrayField,
        newItem,
      });
    },
    [dispatch, sectionIndex]
  );

  // Remove an item from an array field
  const removeArrayItem = useCallback(
    (arrayField, itemIndex) => {
      dispatch({
        type: PLACEMENT_ACTIONS.REMOVE_SECTION_ARRAY_ITEM,
        sectionIndex,
        arrayField,
        itemIndex,
      });
    },
    [dispatch, sectionIndex]
  );

  return {
    updateField,
    updateArrayItem,
    replaceArrayItem,
    updateNestedArrayItem,
    addArrayItem,
    removeArrayItem,
  };
}
